import { useCallback, useEffect, useState } from "react";
import { Award, Download, Loader2, PenLine, Plus } from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { DateField, isoDay } from "@/components/common/DateField";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { shortDate } from "@/lib/format";

const STATUS_STYLE = {
  issued: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  prepared: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  withdrawn: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
};

const yearBack = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return isoDay(d);
};

const Badge = ({ hours }) => (
  <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 font-mono text-[10px] text-primary">
    <Award className="h-3 w-3" /> {hours}h
  </span>
);

export default function Recognition() {
  const { can, user } = useAuth();
  const [range, setRange] = useState({ from: yearBack(), to: isoDay(new Date()) });
  const [data, setData] = useState(null);
  const [certs, setCerts] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [form, setForm] = useState({ volunteer_id: "", period_from: yearBack(),
    period_to: isoDay(new Date()), citation: "", title: "Certificate of Volunteering" });
  const [signing, setSigning] = useState(null);
  const [minute, setMinute] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [overview, list] = await Promise.all([
      api.get("/recognition/overview", { params: { date_from: range.from, date_to: range.to } }),
      api.get("/recognition/certificates"),
    ]);
    setData(overview.data);
    setCerts(list.data);
  }, [range]);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const act = async (key, fn, message) => {
    setBusy(key);
    let ok = false;
    try {
      const res = await fn();
      toast.success(typeof message === "function" ? message(res) : message);
      await load();
      ok = true;
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
    return ok;
  };

  const downloadPdf = async (ref) => {
    try {
      const res = await api.get(`/recognition/certificates/${ref}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${ref}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data || !certs) return <Loading label="Loading recognition" />;
  const canPrepare = can("volunteer", "write");
  const canSign = can("volunteer", "sign");

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Phase 3 · Volunteer recognition"
        title="Hours, milestones and certificates"
        description="Only approved hours from the shared timesheet count. A certificate is prepared by the volunteer coordinator, then signed by the President / Chairperson and the Board Secretary before it can be issued."
        actions={canPrepare && (
          <Button data-testid="prepare-certificate-button" onClick={() => setPreparing(true)}
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <Plus className="mr-1.5 h-4 w-4" /> Prepare a certificate
          </Button>
        )}
      />

      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1.5">
          <Label className="text-[11px] text-muted-foreground">From</Label>
          <DateField value={range.from} testId="recognition-from-input" clearable={false}
                     onChange={(v) => setRange({ ...range, from: v })}
                     className="h-8 w-[150px] text-[11.5px]" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] text-muted-foreground">To</Label>
          <DateField value={range.to} testId="recognition-to-input" clearable={false}
                     onChange={(v) => setRange({ ...range, to: v })}
                     className="h-8 w-[150px] text-[11.5px]" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi testId="kpi-recognition-hours" label="Approved hours" value={data.counters.hours}
             accent="violet" />
        <Kpi testId="kpi-recognition-active" label="Volunteers active" value={data.counters.volunteers}
             sub={`${data.counters.on_roster} on the roster`} />
        <Kpi testId="kpi-recognition-badges" label="Milestones earned" value={data.counters.badges}
             accent="emerald" />
        <Kpi testId="kpi-recognition-issued" label="Certificates issued"
             value={data.counters.certificates} accent="sky" />
        <Kpi testId="kpi-recognition-waiting" label="Awaiting signature" accent="amber"
             value={data.counters.awaiting_signature} />
      </div>

      <Tabs defaultValue="people">
        <TabsList data-testid="recognition-tabs">
          <TabsTrigger value="people" data-testid="tab-recognition-people">Volunteers</TabsTrigger>
          <TabsTrigger value="certificates" data-testid="tab-recognition-certificates">
            Certificates{certs.counters.prepared ? ` (${certs.counters.prepared})` : ""}
          </TabsTrigger>
          <TabsTrigger value="trend" data-testid="tab-recognition-trend">Trend</TabsTrigger>
        </TabsList>

        <TabsContent value="people" className="mt-4">
          <Panel testId="panel-recognition-people" kicker="Approved hours"
                 title="Who has given their time">
            <Table testId="recognition-table"
                   head={<><Th>Volunteer</Th><Th>Hours in window</Th><Th>Lifetime</Th><Th>Milestone</Th>
                     <Th>Next</Th><Th>Programmes</Th><Th>Last active</Th><Th>Certificates</Th></>}>
              {data.people.length === 0 && <Empty colSpan={8} label="No volunteers on the roster" />}
              {data.people.map((p) => (
                <tr key={p.volunteer_id} className="row-hover"
                    data-testid={`recognition-row-${p.volunteer_id}`}>
                  <Td className="text-[11.5px] font-medium text-foreground">{p.name}
                    <div className="text-[10px] text-muted-foreground">{p.role.replace(/_/g, " ")}</div></Td>
                  <Td className="font-mono text-[11px]">{p.hours}</Td>
                  <Td className="font-mono text-[11px]">{p.lifetime_hours}</Td>
                  <Td>{p.milestone.badge ? <Badge hours={p.milestone.badge} />
                    : <span className="text-[11px] text-muted-foreground">—</span>}</Td>
                  <Td className="text-[10.5px] text-muted-foreground">
                    {p.milestone.next ? `${p.milestone.to_next}h to ${p.milestone.next}h` : "top badge"}
                  </Td>
                  <Td className="text-[10.5px]">{p.programmes.join(", ") || "—"}</Td>
                  <Td className="font-mono text-[10.5px]">
                    {p.last_active ? shortDate(p.last_active) : "—"}</Td>
                  <Td className="whitespace-nowrap">
                    <span className="mr-2 font-mono text-[11px]">{p.certificates}</span>
                    {canPrepare && p.hours > 0 && (
                      <Button size="sm" variant="outline" data-testid={`prepare-${p.volunteer_id}`}
                              onClick={() => { setForm({ ...form, volunteer_id: p.volunteer_id,
                                period_from: range.from, period_to: range.to }); setPreparing(true); }}
                              className="h-7 rounded-full border-border px-2.5 text-[10px]">
                        Prepare</Button>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="certificates" className="mt-4">
          <Panel testId="panel-certificates" kicker="Prepared, signed, issued"
                 title="Certificate register">
            <Table testId="certificates-table"
                   head={<><Th>Reference</Th><Th>Volunteer</Th><Th>Period</Th><Th>Hours</Th>
                     <Th>Status</Th><Th>Signatures</Th><Th /></>}>
              {certs.items.length === 0 && <Empty colSpan={7} label="No certificates prepared yet" />}
              {certs.items.map((c) => (
                <tr key={c.certificate_ref} className="row-hover"
                    data-testid={`certificate-row-${c.certificate_ref}`}>
                  <Td className="font-mono text-[10.5px]">{c.certificate_ref}</Td>
                  <Td className="text-[11.5px] text-foreground">{c.volunteer_name}
                    {c.citation && (
                      <div className="text-[10px] text-muted-foreground">{c.citation}</div>
                    )}</Td>
                  <Td className="font-mono text-[10.5px]">
                    {shortDate(c.period_from)} → {shortDate(c.period_to)}</Td>
                  <Td className="font-mono text-[11px]">{c.hours}</Td>
                  <Td><Chip testId={`certificate-status-${c.certificate_ref}`}
                            className={STATUS_STYLE[c.status]}>{c.status}</Chip></Td>
                  <Td className="text-[10.5px]">
                    {(c.signatures || []).length === 0 ? "none yet"
                      : c.signatures.map((s) => (
                        <div key={s.email} className="text-muted-foreground">
                          {s.office} · {s.signed_at.slice(0, 10)}
                        </div>
                      ))}
                  </Td>
                  <Td className="space-x-1.5 whitespace-nowrap">
                    {canSign && c.status === "prepared"
                      && !(c.signatures || []).some((s) => s.email === user?.email) && (
                      <Button size="sm" variant="outline" data-testid={`sign-${c.certificate_ref}`}
                              onClick={() => { setSigning(c); setMinute(""); }}
                              className="h-7 rounded-full border-border px-2.5 text-[10px]">
                        <PenLine className="mr-1 h-3 w-3" /> Sign</Button>
                    )}
                    {c.status === "issued" && (
                      <Button size="sm" variant="outline" data-testid={`pdf-${c.certificate_ref}`}
                              onClick={() => downloadPdf(c.certificate_ref)}
                              className="h-7 rounded-full border-border px-2.5 text-[10px]">
                        <Download className="mr-1 h-3 w-3" /> PDF</Button>
                    )}
                    {canPrepare && c.status === "prepared" && (
                      <Button size="sm" variant="outline" data-testid={`withdraw-${c.certificate_ref}`}
                              onClick={() => act(`w-${c.certificate_ref}`,
                                () => api.post(`/recognition/certificates/${c.certificate_ref}/withdraw`,
                                  { reason: "Withdrawn before issue by the coordinator" }),
                                "Certificate withdrawn")}
                              className="h-7 rounded-full border-border px-2.5 text-[10px] text-red-600 dark:text-red-400">
                        Withdraw</Button>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="trend" className="mt-4 space-y-4">
          <Panel testId="panel-recognition-trend" kicker="Approved hours" title="Month by month">
            <div className="h-[240px] px-3 py-3" data-testid="recognition-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={28} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", fontSize: 11,
                    border: "1px solid hsl(var(--border))", borderRadius: 10 }} />
                  <Bar dataKey="hours" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
          <Panel testId="panel-recognition-programmes" kicker="Where the time went"
                 title="Hours by programme">
            <Table testId="recognition-programme-table" head={<><Th>Programme</Th><Th>Hours</Th></>}>
              {data.by_programme.length === 0 && <Empty colSpan={2} label="No approved hours yet" />}
              {data.by_programme.map((r) => (
                <tr key={r.label} data-testid={`recognition-programme-${r.label}`}>
                  <Td className="text-[11.5px] text-foreground">{r.label}</Td>
                  <Td className="font-mono text-[11px]">{r.hours}</Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>
      </Tabs>

      <Dialog open={preparing} onOpenChange={setPreparing}>
        <DialogContent className="max-w-lg border-border bg-card" data-testid="prepare-dialog">
          <DialogHeader>
            <DialogTitle>Prepare a certificate</DialogTitle>
            <DialogDescription className="text-[11.5px] text-muted-foreground">
              Only approved hours in the period are counted. Once prepared it needs both office
              bearers to sign before it can be issued.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-[11px] text-muted-foreground">Volunteer</Label>
              <Select value={form.volunteer_id}
                      onValueChange={(v) => setForm({ ...form, volunteer_id: v })}>
                <SelectTrigger data-testid="certificate-volunteer-select" className="bg-muted/40">
                  <SelectValue placeholder="Pick a volunteer" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {data.people.map((p) => (
                    <SelectItem key={p.volunteer_id} value={p.volunteer_id}>
                      {p.name} · {p.hours}h in window
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] text-muted-foreground">Period from</Label>
              <DateField value={form.period_from} testId="certificate-from-input" clearable={false}
                         onChange={(v) => setForm({ ...form, period_from: v })}
                         className="h-9 w-full text-[11.5px]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] text-muted-foreground">Period to</Label>
              <DateField value={form.period_to} testId="certificate-to-input" clearable={false}
                         onChange={(v) => setForm({ ...form, period_to: v })}
                         className="h-9 w-full text-[11.5px]" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-[11px] text-muted-foreground">Title</Label>
              <Input value={form.title} data-testid="certificate-title-input"
                     onChange={(e) => setForm({ ...form, title: e.target.value })}
                     className="bg-muted/40 text-[12px]" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-[11px] text-muted-foreground">Citation (printed on the certificate)</Label>
              <Textarea rows={2} value={form.citation} data-testid="certificate-citation-input"
                        onChange={(e) => setForm({ ...form, citation: e.target.value })}
                        className="bg-muted/40 text-[12px]" />
            </div>
          </div>
          <Button data-testid="certificate-save-button" disabled={busy === "prep" || !form.volunteer_id}
                  onClick={async () => {
                    const ok = await act("prep",
                      () => api.post("/recognition/certificates", form),
                      (res) => `${res.data.certificate_ref} prepared for ${res.data.volunteer_name} — ${res.data.hours} hours`);
                    if (ok) setPreparing(false);
                  }}
                  className="h-9 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {busy === "prep" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Prepare the certificate
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(signing)} onOpenChange={(o) => !o && setSigning(null)}>
        <DialogContent className="max-w-md border-border bg-card" data-testid="sign-dialog">
          <DialogHeader>
            <DialogTitle>Sign {signing?.certificate_ref}</DialogTitle>
            <DialogDescription className="text-[11.5px] text-muted-foreground">
              {signing?.volunteer_name} · {signing?.hours} approved hours. Your name and office are
              printed on the certificate; the second signature issues it.
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={2} value={minute} data-testid="sign-minute-input"
                    onChange={(e) => setMinute(e.target.value)}
                    placeholder="A short minute (optional)" className="bg-muted/40 text-[12px]" />
          <Button data-testid="sign-confirm" disabled={busy === "sign"}
                  onClick={async () => {
                    const ok = await act("sign",
                      () => api.post(`/recognition/certificates/${signing.certificate_ref}/sign`,
                        { minute }),
                      (res) => res.data.issued
                        ? `${signing.certificate_ref} issued — the PDF is ready`
                        : `Signed. Still waiting on ${res.data.awaiting.join(" and ")}`);
                    if (ok) setSigning(null);
                  }}
                  className="h-9 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {busy === "sign" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Sign the certificate
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
